import { useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Button, Icon, Text } from '@nula/core-ui';
import { MarkdownRenderer } from './markdown-renderer';
import styles from './approval-card.module.css';

const PREVIEW_CHAR_LIMIT = 300;

/**
 * The rich approval payload cortex emits as a `data-approval-detail` chunk.
 * Shape matches apps/cortex/cortex/server/run_session/vercel_chunk_serializer.py:174-184.
 *
 * Exported because AssistantMessageList builds these from messages[].parts and
 * passes them through to ApprovalCard.
 */
export interface DataApprovalDetail {
  approvalId: string;
  toolName: string;
  summary: string;
  args: Record<string, unknown>;
  items?: string[];
  contentPreview?: ContentPreview | null;
}

export interface ContentPreview {
  // Field names align to the entities-migration nomenclature used everywhere
  // else in the codebase. Server source of truth:
  // apps/cortex/cortex/engine/nodes/approve.py:_build_preview.
  newEntity?: { title: string; contentMarkdown: string; kind?: string };
  update?: { entityId: string; entityTitle: string; beforeContent: string; afterContent: string };
  append?: { entityId: string; entityTitle: string; appendedContent: string };
}

function ContentPreviewBlock({ preview }: { preview: ContentPreview }) {
  const [expanded, setExpanded] = useState(false);

  let previewTitle = '';
  let previewText = '';
  if (preview.newEntity) {
    previewTitle = preview.newEntity.title;
    previewText = preview.newEntity.contentMarkdown;
  } else if (preview.update) {
    previewTitle = preview.update.entityTitle;
    previewText = preview.update.afterContent;
  } else if (preview.append) {
    previewTitle = preview.append.entityTitle;
    previewText = preview.append.appendedContent;
  }

  if (!previewText) return null;

  const isTruncated = previewText.length > PREVIEW_CHAR_LIMIT;
  const displayText = expanded || !isTruncated ? previewText : previewText.slice(0, PREVIEW_CHAR_LIMIT) + '…';

  return (
    <Flex vertical gap={0} className={styles.preview}>
      {previewTitle && <Text className={styles.previewTitle}>{previewTitle}</Text>}
      <MarkdownRenderer className={styles.previewText}>{displayText}</MarkdownRenderer>
      {isTruncated && (
        <Button
          className={styles.previewToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Flex>
  );
}

const root = cva(styles.root, {
  variants: {
    stale: {
      true: styles.stale,
      false: null,
    },
  },
  defaultVariants: {
    stale: false,
  },
});

interface ApprovalCardProps {
  /** The approval ID from the SDK's tool-approval-request chunk. The parent extracts this from
   *  `part.approval.id` of a tool part in state 'approval-requested'. */
  approvalId: string;
  /** The cortex-specific rich payload from `data-approval-detail`, paired by approvalId.
   *  May be `null` when the strict approval chunk has arrived but the rich detail chunk hasn't
   *  yet — the card renders with the buttons but no title/items/preview in that case. */
  detail: DataApprovalDetail | null;
  onApprove?: (approvalId: string) => void;
  onCancel?: (approvalId: string) => void;
  /** When true, the card has been superseded — buttons are disabled and card is dimmed. */
  stale?: boolean;
  /** When set, the card was already resolved (server replay after page reload,
   *  or via SDK part state==='approval-responded'). Renders the receipt
   *  variant: disabled buttons + Approved/Cancelled status label. */
  settled?: { approved: boolean };
  className?: string;
}

/**
 * Renders an approval request card paired from two Vercel SDK parts:
 *   - `tool-*` or `dynamic-tool` part in state 'approval-requested' (carries approvalId via
 *     part.approval.id, drives addToolApprovalResponse)
 *   - `data-approval-detail` part (cortex-specific rich content)
 *
 * The parent (AssistantMessageList) does the pairing by approvalId and passes
 * `approvalId` + `detail` separately. Approve/Cancel callbacks fire with the
 * approvalId so the consumer can call addToolApprovalResponse({ id, approved }).
 *
 * Preview is a UI-only inline expansion of the contentPreview block (Show
 * more / Show less). It does NOT round-trip the server — matches design.md
 * lines 744-748 and the M12a hard-locked decision.
 */
export function ApprovalCard({
  approvalId,
  detail,
  onApprove,
  onCancel,
  stale = false,
  settled,
  className,
}: ApprovalCardProps) {
  // Local "already responded" flag. The SDK's addToolApprovalResponse is
  // async — the messages array update lags the click. Without a local
  // guard the buttons stay clickable in that gap and users send a
  // duplicate response. responded is set synchronously on the first click.
  // It also locally drives the settled-state UI so the receipt appears
  // immediately on click, before the SDK state transition lands.
  const [respondedLocally, setRespondedLocally] = useState<null | { approved: boolean }>(null);

  // Settled = server-replayed (via `settled` prop) OR user clicked locally.
  const settledState = settled ?? respondedLocally;
  const disabled = stale || settledState !== null;

  const handleApprove = () => {
    console.info('[ApprovalCard] handleApprove click', { approvalId, disabled, settledState, stale });
    if (disabled) return;
    setRespondedLocally({ approved: true });
    onApprove?.(approvalId);
  };

  const handleCancel = () => {
    console.info('[ApprovalCard] handleCancel click', { approvalId, disabled, settledState, stale });
    if (disabled) return;
    setRespondedLocally({ approved: false });
    onCancel?.(approvalId);
  };

  return (
    <Flex vertical gap="sm" className={clsx(root({ stale }), className)}>
      {detail?.summary && (
        <MarkdownRenderer className={styles.title}>{detail.summary}</MarkdownRenderer>
      )}

      {detail?.items && detail.items.length > 0 && (
        <Flex vertical gap={0} className={styles.itemList}>
          {detail.items.map((item) => (
            <Flex key={item} align="start" gap="sm" className={styles.item}>
              <Text className={styles.itemDot}>●</Text>
              <Text>{item}</Text>
            </Flex>
          ))}
        </Flex>
      )}

      {detail?.contentPreview && <ContentPreviewBlock preview={detail.contentPreview} />}

      <Flex wrap gap="xs" align="center" className={styles.actions}>
        <Button
          className={styles.btnApprove}
          onClick={handleApprove}
          disabled={disabled}
          data-settled={settledState?.approved === true ? 'true' : undefined}
        >
          {settledState?.approved === true ? (
            <Flex align="center" gap="xs">
              <Icon name="check" size={16} aria-hidden />
              <span>Approved</span>
            </Flex>
          ) : (
            'Approve'
          )}
        </Button>
        <Button
          className={styles.btnCancel}
          onClick={handleCancel}
          disabled={disabled}
          data-settled={settledState?.approved === false ? 'true' : undefined}
        >
          {settledState?.approved === false ? (
            <Flex align="center" gap="xs">
              <Icon name="close" size={16} aria-hidden />
              <span>Cancelled</span>
            </Flex>
          ) : (
            'Cancel'
          )}
        </Button>
      </Flex>
    </Flex>
  );
}
